<template lang="pug">
    .impressions-map
        .map-holder
            .map
</template>

<script>
import _ from 'lodash';
import $ from 'jquery';
import dynamic from 'dynamic';
import VueTypes from 'vue-types';
import Data from 'app-core/Data';
import api from '@/view/carto/api.js';

export default {
    translationCategory: 'dashboard.ImpressionsMap',
    name: 'impressions-map',
    components: {},
    props: {
        items: VueTypes.array.def([]),
        quoteNumber: VueTypes.any.def(null)
    },
    data() {
        return {
            maxClicks: 0,
            maxImpressions: 0
        };
    },
    computed: {
        L() {
            return window.L;
        }
    },
    watch: {
        items() {
            this.addItems();
        },
        quoteNumber() {
            this.buildCartoLayer();
        }
    },
    mounted() {
        window.jQuery = $;
        dynamic
            .loadOnce([
                'https://unpkg.com/leaflet@1.5.1/dist/leaflet.js',
                'https://unpkg.com/leaflet@1.5.1/dist/leaflet.css',
            ])
            .then(() => {
                dynamic
                    .loadOnce([
                        'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/0.4.13/leaflet.draw.js',
                        'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/0.4.13/leaflet.draw.css',
                        'https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.4.1/leaflet.markercluster.js',
                        'https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.4.1/MarkerCluster.Default.css',
                        'https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.4.1/MarkerCluster.css',
                        'https://cdnjs.cloudflare.com/ajax/libs/animate.css/3.7.2/animate.min.css',
                        'https://libs.cartocdn.com/carto.js/v4.2.0/carto.min.js',
                        'https://carto.com/developers/carto-js/examples/maps/public/style.css'
                    ])
                    .then(() => {
                        this.buildMap();
                    });
            });
    },
    destroyed() {
        delete this.$layer;
        delete this.$map;
    },
    methods: {
        autoStyleFieldChanged(field, name) {
            this.autoStyleField = field;
            this.autoStyleFieldCaption = name;
            this.createLayers();
        },
        buildMap() {
            if (__DEBUG__)
                console.log('Build impressions');
            
            const map = this.$map = this.L.map($(this.$el).find('.map')[0]).setView([37.8, -96], 5);
            
            this.L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}@2x.png', {
                maxZoom: 18
            }).addTo(map);
            
            this.buildCartoLayer();
            this.addItems();
        },
        buildCartoLayer() {
            const carto = window.carto;
            
            const client = this.$client = api.getClient(true);
            
            if (this.$currentCartoLayer) {
                this.$map.removeLayer(this.$currentCartoLayer);
            }
            
            if (this.$mapLayer) {
                client.removeLayer(this.$mapLayer);
            }
            
            const mapSource = this.$mapSource = new carto.source.SQL('select * from alluszipswithdata');
            //mapSource.setQuery(api.buildQuery(this.proposal.regions, void 0, void 0, { where: this.proposal.regionsWhereClause }));
            
            const mapStyle = new carto.style.CartoCSS(`
					#layer {
						polygon-fill: ramp([a_med_in_1], (#ecda9a,#f1b973,#f7945d,#f86f56,#ee4d5a), jenks);
						polygon-opacity: 0.6;
					}
					#layer::outline {
						line-width: 0.5;
						line-color: #f27649;
						line-opacity: 0.6;
					}
					#layer::labels [zoom>8]{
						text-name: [zcta5ce10];
						text-face-name: 'DejaVu Sans Book';
						text-size: 10;
						text-fill: rgba(0,0,0,0.5);
						text-label-position-tolerance: 0;
						text-halo-radius: 1;
						text-halo-fill: rgba(255,255,255,0.3);
						text-dy: -10;
						text-allow-overlap: false;
						text-placement: point;
						text-placement-type: dummy;
					}
				`);
            
            const mapLayer = this.$mapLayer = new carto.layer.Layer(this.$mapSource, mapStyle);
            client.addLayer(mapLayer);
            
            this.$currentCartoLayer = client.getLeafletLayer();
            this.$currentCartoLayer.addTo(this.$map);
        },
        addItems() {
            if (!this.$map) return;
            if (this.$layer)
                this.$map.removeLayer(this.$layer);
            if (!this.items.length) return;
            
            const items = this.items.map(f => ({
                'type': 'Feature',
                'properties': f,
                'geometry': {
                    'type': 'Point',
                    'coordinates': [parseFloat(f.location.geo.lng), parseFloat(f.location.geo.lat)]
                }
            }));
            
            this.maxClicks = _.maxBy(this.items, v => v.clicks).clicks;
            this.maxImpressions = _.maxBy(this.items, v => v.clicks).impressions;
            
            if (__DEBUG__)
                console.log('Add items to impressions map', { items });
            
            const markerLayer = this.L.geoJSON(items);
            
            this.$layer = this.L.markerClusterGroup({
                singleMarkerMode: true,
                spiderfyOnMaxZoom: false,
                showCoverageOnHover: false,
                zoomToBoundsOnClick: false,
                iconCreateFunction: (cluster) => {
                    var markers = cluster.getAllChildMarkers();
                    var totalImpressions = _.sumBy(markers, v => v.feature.properties.impressions);
                    
                    var size = Math.min(80, Math.max(20, 100 * parseFloat(totalImpressions) / this.maxImpressions));
                    
                    return this.L.divIcon({
                        html: `<b>${Data.formatNumber(totalImpressions, 'fixedPoint')}</b>`,
                        iconSize: this.L.point(size, size),
                        className: 'leaflet-marker-icon marker-cluster cluster-marker fade-in'
                    });
                }
            });
            
            this.$layer.on('clusterclick', (v) => {
                const zips = v.layer.getAllChildMarkers()
                    .map(f => f.feature.properties.zip).reduce((arr, f) => {
                        if (arr.indexOf(f) === -1)
                            arr.push(f);
                        return arr;
                    }, []);
                this.$emit('select-zips', zips);
            });
            this.$layer.on('click', (v) => {
                this.$emit('select-zips', [v.layer.feature.properties.zip]);
            });
            
            this.$layer.addLayer(markerLayer);
            this.$map.addLayer(this.$layer);
        }
    }
};
</script>
<style lang='scss'>
.impressions-map {
    .map-holder {
        height: calc(100vh - 513px);
        width: 100%;
        max-height: 65vh;
        min-height: 440px;
        margin-top: 0px;
        position: relative;
        
        .map {
            width: 100%;
            height: 100%;
        }
        
        > .proposal-buttons-group {
            position: absolute;
            left: 10px;
            bottom: 10px;
        }
        
        .cluster-marker {
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 100px;
            background: #f27649ab;
            color: #ffebe4;
            border: 2px solid #f27649ab;
        }
    }
    
    .fade-in {
        -webkit-animation: fade-in 0.4s cubic-bezier(0.390, 0.575, 0.565, 1.000) both;
        animation: fade-in 0.4s cubic-bezier(0.390, 0.575, 0.565, 1.000) both;
    }
    
    @-webkit-keyframes fade-in {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
    @keyframes fade-in {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
}
</style>
